import { Injectable } from '@angular/core';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { Permission } from 'src/app/models/permissions/permissions.const';
import {
  AIRuntimeFindingsQueryParams,
  HygieneDefaultQueryParams,
  InventoryDefaultQueryParams,
  ViolationsDefaultQueryParams,
} from 'src/app/models/constants/navigation.constants';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { IAppPage } from 'src/app/models/navigation/navigation.interface';

type FeatureFlagCondition = { flag: FeatureFlagTypes; hideWhenEnabled: boolean };

const showWhen = (flag: FeatureFlagTypes): FeatureFlagCondition => ({ flag, hideWhenEnabled: false });
const hideWhen = (flag: FeatureFlagTypes): FeatureFlagCondition => ({ flag, hideWhenEnabled: true });

@Injectable({
  providedIn: 'root',
})
export class NavigationConfigService {
  constructor(
    private readonly _featureFlagService: FeatureFlagsService,
    private readonly _permissionsService: PermissionsService
  ) {}

  public getAppPages(): IAppPage[] {
    return [
      this._menuItem('dashboard', 'menu.dashboardPage', 'Overview', 'lucide:gauge', null, null, null, '/app'),

      this._menuItem(
        'issues',
        'menu.issuesPage',
        'Issues',
        undefined,
        showWhen(FeatureFlagTypes.IssuesPage),
        null,
        null,
        '/app/issues'
      ),

      this._menuItem(
        'ai-observability',
        'menu.aiObservabilityPage',
        'AI Observability',
        'lucide:eye',
        null,
        null,
        null,
        '',
        undefined,
        [
          this._menuItem(
            'inventory',
            'menu.inventoryPage',
            'Inventory',
            '',
            null,
            { q: InventoryDefaultQueryParams },
            null,
            '/app/inventory'
          ),
          this._menuItem(
            'ai-activity',
            'menu.aiDetectionActivityPage',
            'Activity',
            '',
            showWhen(FeatureFlagTypes.EnterpriseAiDetection),
            null,
            null,
            '/app/ai-activity'
          ),
          this._menuItem(
            'ai-data-insights',
            'menu.aiDetectionDataInsightsPage',
            'Data Insights',
            '',
            showWhen(FeatureFlagTypes.AIRuntimeDataInsightsTab),
            null,
            null,
            '/app/ai-data-insights'
          ),
          this._menuItem(
            'data-lens',
            'menu.dataLensPage',
            'Data Layer',
            '',
            showWhen(FeatureFlagTypes.DataLayerPage),
            null,
            null,
            '/app/data-lens'
          ),
          this._menuItem(
            'adoption',
            'menu.adoptionPage',
            'Adoption',
            '',
            showWhen(FeatureFlagTypes.AdoptionDashboard),
            null,
            null,
            '/app/adoption'
          ),
        ]
      ),

      this._menuItem('ai-spm', 'menu.aiSpmPage', 'AI-SPM', '', null, null, null, '', undefined, [
        this._menuItem(
          'violations',
          'menu.violationsPage',
          'Violations',
          '',
          null,
          { q: ViolationsDefaultQueryParams },
          null,
          '/app/violations'
        ),
        this._menuItem(
          'hygiene',
          'menu.hygienePage',
          'Hygiene',
          '',
          null,
          { q: HygieneDefaultQueryParams },
          null,
          '/app/hygiene'
        ),
      ]),

      this._menuItem(
        'aidr',
        'menu.aiDrPage',
        'AI-DR',
        '',
        showWhen(FeatureFlagTypes.EnterpriseAiDetection),
        null,
        null,
        '',
        undefined,
        [
          this._menuItem(
            'ai-findings',
            'menu.aiDetectionFindingsPage',
            'Findings',
            '',
            null,
            { q: AIRuntimeFindingsQueryParams },
            null,
            '/app/ai-findings'
          ),
          this._menuItem(
            'ai-in-line-control',
            'menu.aiDetectionInLineControlPage',
            'In-Line-Control',
            '',
            this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.ReactHideInlineControlPage)
              ? hideWhen(FeatureFlagTypes.ReactHideInlineControlPage)
              : showWhen(FeatureFlagTypes.InlinePrevention),
            null,
            null,
            '/app/ai-in-line-control'
          ),
        ]
      ),

      this._menuItem(
        'governance',
        'menu.governancePage',
        'Governance',
        'lucide:scroll',
        null,
        null,
        null,
        '',
        undefined,
        [
          this._menuItem(
            'policies',
            'menu.policyPage',
            'Policies',
            'lucide:book-open',
            hideWhen(FeatureFlagTypes.BlockPolicyPage),
            null,
            null,
            '/app/policies'
          ),
          this._menuItem(
            'playbooks',
            'menu.playbooksPage',
            'Playbooks',
            'lucide:book-open',
            null,
            null,
            null,
            '/app/playbooks'
          ),
          this._menuItem(
            'attack-simulations',
            'menu.redTeamingPage',
            'AI Red Teaming',
            'lucide:shield-alert',
            showWhen(FeatureFlagTypes.RedTeaming),
            null,
            null,
            '/app/attack-simulations'
          ),
        ]
      ),
      this._menuItem(
        'boundaries',
        'menu.boundariesPage',
        'Boundaries',
        'lucide:octagon-x',
        showWhen(FeatureFlagTypes.ReactRsa2026DemoRuntimeBoundaries),
        null,
        null,
        '/app/boundaries'
      ),

      ...(this._featureFlagService.isFeatureEnabled(FeatureFlagTypes.EnableAdaptersLayout)
        ? [this._menuItem('connect', 'menu.connectPage', 'Connect', 'lucide:plug', null, null, null, '/app/connect')]
        : [
            this._menuItem(
              'integrations',
              'menu.integrationsPage',
              'Integrations',
              'lucide:plug',
              null,
              null,
              null,
              '/app/integrations'
            ),
          ]),
      this._menuItem(
        'management',
        'menu.settingsPage',
        'Settings',
        'lucide:settings',
        null,
        null,
        null,
        '/app/management',
        undefined,
        undefined,
        true
      ),
    ].filter((page) => !page.hidden);
  }

  public getSupportSectionPages(): IAppPage[] {
    return [
      this._menuItem('docs', 'menu.docsPage', 'Docs', 'lucide:book-open', null, null, null, '', undefined, [
        this._menuItem(
          'product-guides',
          'menu.productGuides',
          'Product guides',
          '',
          null,
          null,
          null,
          this._getDocsUrl()
        ).also((productGuides) => {
          productGuides.newTab = true;
          productGuides.navigationTo = this._getDocsUrl();
        }),
        this._menuItem(
          'api-reference',
          'menu.apiReference',
          'API reference',
          '',
          null,
          null,
          null,
          this._getRedocUrl()
        ).also((apiReference) => {
          apiReference.newTab = true;
          apiReference.navigationTo = this._getRedocUrl();
        }),
      ]),
    ];
  }

  private _getDocsUrl(): string {
    const host = window.location.host;
    return host.includes('us1') ? 'https://docs.us1.zenity.io' : 'https://docs.zenity.io/';
  }

  private _getRedocUrl(): string {
    const host = window.location.host;
    return host.includes('us1') ? 'https://app.us1.zenity.io/redoc' : 'https://app.zenity.io/redoc';
  }

  private _menuItem = (
    id: string,
    labelPath: string,
    displayName: string,
    iconPath: string | undefined = undefined,
    featureFlag: FeatureFlagCondition | null = null,
    navigationQuery: unknown | null = null,
    permission: Permission | null = null,
    navigateTo: string = id,
    titleIconPath: string | undefined = undefined,
    children: IAppPage[] | undefined = undefined,
    adminOnly = false
  ): IAppPage => ({
    displayName: displayName,
    displayTitleLabelPath: labelPath,
    iconPath: iconPath ? iconPath : `assets/images/menu-icons/${id}.svg`,
    navigationTo: navigateTo,
    navigationQuery: navigationQuery || {},
    newTab: false,
    isActive: false,
    selected: false,
    isExpanded: false,
    hidden:
      (adminOnly && !this._permissionsService.isAdmin()) ||
      (permission?.let((p) => !this._permissionsService.userHasPermission(p)) ?? false) ||
      (featureFlag?.let(({ flag, hideWhenEnabled }) => this._shouldHideByFeatureFlag(flag, hideWhenEnabled)) ?? false),
    titleIconPath: titleIconPath,
    children: children,
    isParent: !!children && children.length > 0,
    isReactRoute: this._isReactRoute(navigateTo),
  });

  private _isReactRoute(navigateTo: string): boolean {
    const routeKey = navigateTo.replace(/^\/app\/?/, '').split('/')[0];
    return this._featureFlagService.isReactRouteEnabled(routeKey);
  }

  private _shouldHideByFeatureFlag(flag: FeatureFlagTypes, hideWhenEnabled: boolean): boolean {
    const enabled = this._featureFlagService.isFeatureEnabled(flag);
    return hideWhenEnabled ? enabled : !enabled;
  }
}
